import { createContext, useEffect, useState } from "react";
import axios from "axios";
import Modal from "../Modal";

export const AuthContext = createContext();

export const AuthContextProvider = ({ children }) => {
  const getStoredUser = () => {
    try {
      const storedUser = localStorage.getItem("user");
      return storedUser && storedUser !== "undefined" ? JSON.parse(storedUser) : null;
    } catch (error) {
      console.error("Error parsing user data from localStorage", error);
      return null;
    }
  };

  const storedAccessToken = localStorage.getItem("accessToken");
  const storedRole = localStorage.getItem("role");

  const [currentUser, setCurrentUser] = useState(getStoredUser());
  const [accessToken, setAccessToken] = useState(storedAccessToken || null);
  const [role, setRole] = useState(storedRole || null);
  const [showModal, setShowModal] = useState(false); // Modal visibility state
  const [modalMessage, setModalMessage] = useState(""); // Modal message state

  const login = async (email, password) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/login`,
        { email, password },
        { withCredentials: true }
      );

      const { token, user } = response.data;

      // Store token separately
      localStorage.setItem("accessToken", token);
      setAccessToken(token);

      // Ensure user is stored correctly
      if (user) {
        localStorage.setItem("user", JSON.stringify(user));
        setCurrentUser(user);

        if (user.role) {
          localStorage.setItem("role", user.role);
          setRole(user.role);
        }
      }
    } catch (error) {
      console.error("Login failed", error);
      throw error;
    }
  };

  const register = async (username, email, password, referralId) => {
    try {
      let url = `${process.env.REACT_APP_API_BASE_URL}/register`;
      if (referralId) {
        url += `?ref=${encodeURIComponent(referralId)}`;
      }
  
      const response = await axios.post(
        url,
        { username, email, password }, 
        { withCredentials: true }
      );
  
      const { token, user } = response.data;
  
      // Store token in localStorage
      localStorage.setItem("accessToken", token);
      setAccessToken(token);
  
      // Store user data
      if (user) {
        localStorage.setItem("user", JSON.stringify(user));
        setCurrentUser(user);
  
        if (user.role) {
          localStorage.setItem("role", user.role);
          setRole(user.role);
        }
      }
    } catch (error) {
      console.error("Registration failed", error);
      throw error;
    }
  };  

  const logout = async () => {
    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Error logging out:", error);
    }

    // Clear localStorage and reset states
    localStorage.removeItem("user");
    localStorage.removeItem("accessToken");
    localStorage.removeItem("role");

    setCurrentUser(null);
    setAccessToken(null);
    setRole(null);
  };

  useEffect(() => {
    // Set up axios response interceptor for token expiration
    const interceptor = axios.interceptors.response.use(
      (response) => response, // Pass the response through if no errors
      (error) => {
        if (error.response && error.response.status === 401) {
          // Token has expired or user is unauthorized
          setModalMessage("Your session has expired");
          setShowModal(true); // Show modal
          setTimeout(() => {
            logout(); // Perform the logout after showing the modal
          }, 3000); // Delay logout by 3 seconds to allow the modal to show
        }
        return Promise.reject(error);
      }
    );

    // Cleanup the interceptor when the component unmounts
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  useEffect(() => {
    if (currentUser) {
      localStorage.setItem("user", JSON.stringify(currentUser));
    } else {
      localStorage.removeItem("user");
    }

    if (accessToken) {
      localStorage.setItem("accessToken", accessToken);
    } else {
      localStorage.removeItem("accessToken");
    }

    if (role) {
      localStorage.setItem("role", role);
    } else {
      localStorage.removeItem("role");
    }
  }, [currentUser, accessToken, role]);

  return (
    <AuthContext.Provider value={{ currentUser, accessToken, role, login, logout, register, setCurrentUser }}>
      {children}
      {showModal && <Modal message={modalMessage} />}
    </AuthContext.Provider>
  );
};
